import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  Building2,
  CreditCard,
  Eye,
  Handshake,
  Instagram,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  ThumbsUp,
  type LucideIcon,
} from 'lucide-react';
import { useKbArticlesInfinite, useKbCategories } from '@/api/kb';
import type { ArticleStatus, KbCategory } from '@/api/types';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  ListSkeleton,
} from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';
import { formatNumber, formatRelative, humanize } from '@/lib/format';
import { useDebounced } from '../tickets/useDebounced';
import { CategoryModal } from './CategoryModal';
import { WebsiteSources } from './WebsiteSources';
import { ARTICLE_TONE } from './meta';

const CATEGORY_ICON: Record<string, LucideIcon> = {
  payments: CreditCard,
  instagram: Instagram,
  collaborations: Handshake,
  listings: Building2,
  account: ShieldCheck,
};

export function KnowledgeBasePage() {
  const { can } = useAuth();
  const canWrite = can('create', 'KnowledgeBase');
  const [params, setParams] = useSearchParams();
  const categoryId = params.get('category') ?? '';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ArticleStatus | ''>('');
  const [editingCat, setEditingCat] = useState<KbCategory | 'new' | null>(null);
  const debouncedQ = useDebounced(q.trim(), 300);

  const categories = useKbCategories();
  const articles = useKbArticlesInfinite({
    q: debouncedQ || undefined,
    categoryId: categoryId || undefined,
    status: status || undefined,
  });
  const rows = articles.data?.pages.flatMap((p) => p.data) ?? [];
  const catById = new Map((categories.data ?? []).map((c) => [c.id, c]));
  const total = (categories.data ?? []).reduce((s, c) => s + c.articleCount, 0);

  return (
    <>
      <PageHeader
        eyebrow="Knowledge"
        title="Knowledge base"
        description="Help articles for creators, businesses and brands — and quick answers for agents."
        actions={
          canWrite && (
            <Link
              to={`/kb/articles/new${categoryId ? `?category=${categoryId}` : ''}`}
              className="from-brand-500 to-brand-600 hover:to-brand-700 shadow-brand-600/25 inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-b px-4 text-sm font-medium text-white shadow-sm ring-1 ring-white/10 ring-inset"
            >
              <Plus className="h-4 w-4" /> New article
            </Link>
          )
        }
      />
      {canWrite && !categoryId && !debouncedQ && (
        <WebsiteSources onOpenCategory={(id) => setParams({ category: id }, { replace: true })} />
      )}
      {!categoryId && !debouncedQ && (categories.data?.length ?? 0) > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {categories.data!.map((c) => {
            const Icon = CATEGORY_ICON[c.slug] ?? BookOpen;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setParams({ category: c.id }, { replace: true })}
                className="group shadow-card hover:shadow-card-hover hover:border-brand-200 rounded-2xl border border-slate-200/70 bg-white p-4 text-left transition-all hover:-translate-y-0.5"
              >
                <span className="from-brand-50 to-accent-50 text-brand-600 ring-brand-100 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ring-1">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <p className="group-hover:text-brand-700 mt-3 font-semibold text-slate-900">{c.name}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{c.description}</p>
                <p className="mt-3 text-xs font-medium text-slate-400">
                  {c.publishedCount} published · {c.articleCount - c.publishedCount} draft
                </p>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-col gap-5 lg:flex-row">
        <nav aria-label="Categories" className="lg:w-60 lg:shrink-0">
          <div className="-mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:px-0">
            <CategoryLink
              label="All articles"
              count={total}
              active={!categoryId}
              onClick={() => setParams({}, { replace: true })}
            />
            {(categories.data ?? []).map((c) => (
              <CategoryLink
                key={c.id}
                label={c.name}
                count={c.articleCount}
                active={categoryId === c.id}
                onClick={() => setParams({ category: c.id }, { replace: true })}
                onEdit={canWrite ? () => setEditingCat(c) : undefined}
              />
            ))}
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditingCat('new')}
              className="text-brand-600 hover:text-brand-700 mt-2 hidden items-center gap-1.5 px-3 text-sm font-medium lg:flex"
            >
              <Plus className="h-4 w-4" /> Add category
            </button>
          )}
        </nav>

        <Card className="min-w-0 flex-1 overflow-hidden">
          {categoryId && catById.get(categoryId)?.description && (
            <p className="border-b border-slate-100 px-4 py-3 text-sm text-slate-500">
              {catById.get(categoryId)!.description}
            </p>
          )}
          <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                aria-label="Search articles"
                placeholder="Search articles…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ArticleStatus | '')}
              wrapperClassName="sm:w-40"
            >
              <option value="">Any status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </Select>
          </div>

          {articles.isPending ? (
            <ListSkeleton label="Loading articles" />
          ) : articles.error ? (
            <ErrorState error={articles.error} onRetry={() => void articles.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<BookOpen className="h-6 w-6" />}
              title={debouncedQ || status ? 'No articles match' : 'No articles here yet'}
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((a) => {
                const votes = a.helpfulCount + a.notHelpfulCount;
                return (
                  <li key={a.id}>
                    <Link to={`/kb/articles/${a.id}`} className="block px-4 py-3.5 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900">{a.title}</p>
                          <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
                            {a.excerpt || 'No summary'}
                          </p>
                        </div>
                        <Badge tone={ARTICLE_TONE[a.status]}>{humanize(a.status)}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                        {!categoryId && <span>{catById.get(a.categoryId)?.name ?? '—'}</span>}
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5" aria-hidden /> {formatNumber(a.views)} views
                        </span>
                        {votes > 0 && (
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="h-3.5 w-3.5" aria-hidden />{' '}
                            {Math.round((a.helpfulCount / votes) * 100)}% helpful
                          </span>
                        )}
                        <span>Updated {formatRelative(a.updatedAt)}</span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {articles.hasNextPage && (
            <div className="flex justify-center border-t border-slate-100 p-3">
              <Button
                variant="ghost"
                size="sm"
                loading={articles.isFetchingNextPage}
                onClick={() => void articles.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}
        </Card>
      </div>
      {editingCat && (
        <CategoryModal
          category={editingCat === 'new' ? null : editingCat}
          onClose={() => setEditingCat(null)}
        />
      )}
    </>
  );
}

function CategoryLink({
  label,
  count,
  active,
  onClick,
  onEdit,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  onEdit?: () => void;
}) {
  return (
    <div
      className={cn(
        'group flex shrink-0 items-center rounded-lg transition-colors',
        active ? 'shadow-card bg-white ring-1 ring-slate-200' : 'hover:bg-white/70',
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex flex-1 items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium whitespace-nowrap',
          active ? 'text-brand-700' : 'text-slate-600',
        )}
      >
        {label}
        <span className={cn('text-xs tabular-nums', active ? 'text-brand-600' : 'text-slate-400')}>
          {count}
        </span>
      </button>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${label}`}
          className="mr-1 hidden rounded p-1 text-slate-400 group-hover:block hover:bg-slate-100 hover:text-slate-700 focus:block"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
